//@flow
import {
    homebaseMaster as homebaseApi,
    bulkHomebaseFetch as bulkHomebaseFetchApi,
    homebaseStatusUpdate as homebaseStatusUpdateApi,
    bulkDisableHomebase as bulkDisableHomebaseApi,
    locationBulkGeocode,
    validateEntityIds as validateEntityIdsApi
} from 'common/middleware/api';
import { showToast } from 'common/redux/actions/toast';
import { ToastTypes } from 'common/components/core/toast/Toast';
import PaginatedEntityActions from 'common/redux/actions/PaginatedEntityActions';
import I18nHelper from 'common/helpers/I18nHelper';

import type { MasterHomebase } from 'common/models/internal/MasterHomebase';
import type { Dispatch } from 'redux';
import { HomebaseStatusEnum } from 'common/models/internal/MasterHomebase';
import type { GeocodeEditLocation } from 'common/models/internal/GeocodeEditLocation';
import { ENTITY_NAME_ID } from 'common/constants/entity/homebaseMaster';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

type Params = {
    clientId: string,
    homebaseId?: string
};

class HomebaseActions extends PaginatedEntityActions<MasterHomebase, Params> {
    entityNameId = ENTITY_NAME_ID;
    superAddEntity = this.addEntity;

    addEntity = (
        params: { clientId: string, homebaseId: string },
        entity: MasterHomebase,
        _options: Object = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (homebase: MasterHomebase) => {
                        const toast = {
                            message: nsTranslate('addEntitySuccessMessage', {
                                entityNameId: 'homebase'
                            }),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(homebase);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError: true
                };
                dispatch(this.superAddEntity(params, entity, options));
            });
        };
    };

    superPatchEntity = this.patchEntity;

    patchEntity = (
        params: { clientId: string, homebaseId: string },
        oldEntity: MasterHomebase,
        newEntity: MasterHomebase,
        _options: { dontInjectError?: boolean, suppressToasts?: boolean } = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const { dontInjectError, suppressToasts } = _options;
                const [oldEntitySanitized, newEntitySanitized] = [
                    oldEntity,
                    newEntity
                ].map(entity => entity.sanitize());
                const options = {
                    onSuccess: (homebase: MasterHomebase) => {
                        if (!suppressToasts) {
                            const toast = {
                                message: nsTranslate('savedChangesMessage'),
                                type: ToastTypes.Success
                            };
                            dispatch(showToast(toast, params));
                        }
                        resolve(homebase);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError
                };
                dispatch(
                    this.superPatchEntity(
                        params,
                        oldEntitySanitized,
                        newEntitySanitized,
                        options
                    )
                );
            });
        };
    };

    entityStatusUpdate = this.entityStatusUpdate;

    disableEntity = (
        params: { clientId: string, homebaseId: string },
        _options: Object = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (homebase: MasterHomebase) => {
                        const toast = {
                            message: nsTranslate(
                                'disableEntitySuccessMessage',
                                {
                                    entityNameId: 'homebase'
                                }
                            ),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(homebase);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    }
                };
                const body = {
                    status: HomebaseStatusEnum.Inactive
                };
                dispatch(this.entityStatusUpdate(params, options, body));
            });
        };
    };

    enableEntity = (
        params: { clientId: string, homebaseId: string },
        _options: Object = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (homebase: MasterHomebase) => {
                        const toast = {
                            message: nsTranslate('enableEntitySuccessMessage', {
                                entityNameId: 'homebase'
                            }),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(homebase);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    }
                };
                const body = {
                    status: HomebaseStatusEnum.Active
                };
                dispatch(this.entityStatusUpdate(params, options, body));
            });
        };
    };

    bulkGeocode = (
        params: { clientId: string },
        locations: Array<GeocodeEditLocation> = [],
        options: Object = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            const locationIds = locations.map(location => location.id);
            const body = { locationIds };
            const { onSuccess, onFailure } = options;
            this.geocodeApi
                .post(params, body)
                .then(() => {
                    onSuccess && onSuccess(locationIds);
                })
                .catch(error => {
                    const toast = {
                        message: error.message,
                        type: ToastTypes.Error
                    };
                    dispatch(showToast(toast, params));
                    onFailure && onFailure();
                });
        };
    };
}

export default new HomebaseActions(
    'Homebase',
    homebaseApi,
    bulkHomebaseFetchApi,
    null,
    bulkDisableHomebaseApi,
    validateEntityIdsApi,
    locationBulkGeocode,
    homebaseStatusUpdateApi
);
